import { Button } from "@/components/ui/button";
import { Menu, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Link } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import headerLogo from "@/assets/ndf-logo-v2.png";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { siteVisibility } from "@/config/siteVisibility";

type NavItem = {
  name: string;
  href: string;
  isExternal?: boolean;
  children?: { name: string; href: string }[];
};

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const { t } = useLanguage();

  const aboutChildren = [
    siteVisibility.pages.expertise && { name: t('nav.expertise'), href: "/expertise" },
    siteVisibility.pages.succesverhalen && { name: t('nav.success'), href: "/succesverhalen" },
  ].filter(Boolean) as { name: string; href: string }[];

  const navigation: NavItem[] = [
    { name: "DataStudent", href: "/datastudent" },
    { name: "New Data Select", href: "/new-data-select" },
    { name: "Dutch Data Company", href: "/dutch-data-company" },
    siteVisibility.pages.companies !== false && { name: t('nav.companies'), href: "/bedrijven" },
    siteVisibility.pages.consultants !== false && { name: t('nav.consultants'), href: "/consultants" },
    siteVisibility.pages.aboutUs && {
      name: t('nav.about'),
      href: "/over-ons",
      ...(aboutChildren.length > 0 && { children: aboutChildren }),
    },
    siteVisibility.pages.blog && { name: "Blog", href: "/blog" },
    siteVisibility.pages.vacatures && { name: t('nav.vacatures'), href: "/vacatures" },
  ].filter(Boolean) as NavItem[];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-primary border-b border-primary-foreground/10">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-28">
          <Link to="/" className="flex items-center gap-3">
            <img src={headerLogo} alt="New Data Frontier" className="h-20 md:h-28" width={112} height={112} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <NavigationMenu>
              <NavigationMenuList className="gap-6">
                {navigation.map((item) =>
                  item.children ? (
                    <NavigationMenuItem key={item.name}>
                      <NavigationMenuTrigger className="text-sm font-medium text-primary-foreground hover:text-accent bg-transparent hover:bg-transparent focus:bg-transparent focus:text-primary-foreground data-[state=open]:bg-transparent data-[state=open]:text-primary-foreground data-[active]:bg-transparent px-0 py-0 h-auto">
                        {item.name}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="w-48 p-2">
                          <li>
                            <Link
                              to={item.href}
                              className="block px-3 py-2 text-sm font-medium rounded-md hover:bg-muted transition-colors"
                              onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))}
                            >
                              {item.name}
                            </Link>
                          </li>
                          {item.children.map((child) => (
                            <li key={child.name}>
                              <Link
                                to={child.href}
                                className="block px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                                onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))}
                              >
                                {child.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  ) : (
                    <NavigationMenuItem key={item.name}>
                      <NavigationMenuLink asChild>
                        <Link
                          to={item.href}
                          className="text-sm font-medium text-primary-foreground hover:text-accent transition-colors"
                        >
                          {item.name}
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  )
                )}
              </NavigationMenuList>
            </NavigationMenu>
            <LanguageSwitcher />
            <Button className="bg-accent hover:bg-accent/90 text-accent-foreground" asChild>
              <a href="/#contact">{t('nav.contact')}</a>
            </Button>
          </div>

          {/* Mobile Navigation */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-primary-foreground hover:text-accent hover:bg-primary-foreground/10">
                <Menu className="h-7 w-7" />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <div className="flex flex-col gap-6 mt-8">
                {navigation.map((item) =>
                  item.children ? (
                    <Collapsible key={item.name} open={aboutOpen} onOpenChange={setAboutOpen}>
                      <CollapsibleTrigger className="flex items-center gap-1 text-lg font-medium text-foreground hover:text-accent transition-colors w-full">
                        {item.name}
                        <ChevronDown className={`h-4 w-4 transition-transform ${aboutOpen ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pl-4 mt-2 flex flex-col gap-3">
                        <Link
                          to={item.href}
                          onClick={() => setIsOpen(false)}
                          className="text-base text-foreground hover:text-accent transition-colors"
                        >
                          {item.name}
                        </Link>
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            onClick={() => setIsOpen(false)}
                            className="text-base text-muted-foreground hover:text-accent transition-colors"
                          >
                            {child.name}
                          </Link>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className="text-lg font-medium text-foreground hover:text-accent transition-colors"
                    >
                      {item.name}
                    </Link>
                  )
                )}
                <LanguageSwitcher />
                <Button className="bg-accent hover:bg-accent/90 text-accent-foreground w-full" asChild>
                  <a href="/#contact">{t('nav.contact')}</a>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Header;
